import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

  .fe-page {
    font-family: 'Inter', system-ui, sans-serif;
    min-height: 100vh;
    background: #0a0f1e;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 32px 20px;
    position: relative;
    overflow: hidden;
  }

  .fe-blob {
    position: fixed; border-radius: 50%; filter: blur(80px);
    pointer-events: none; z-index: 0;
  }
  .fe-blob-1 { width: 480px; height: 480px; background: rgba(99,102,241,0.1);  top: -160px; right: -160px; }
  .fe-blob-2 { width: 380px; height: 380px; background: rgba(79,70,229,0.07);  bottom: -100px; left: -100px; }

  .fe-card {
    position: relative; z-index: 1;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(99,102,241,0.18);
    border-radius: 24px;
    padding: 48px 40px;
    width: 100%; max-width: 440px;
    box-shadow: 0 24px 60px rgba(0,0,0,0.4);
    backdrop-filter: blur(18px);
    animation: feFadeUp 0.45s ease-out both;
  }

  @keyframes feFadeUp {
    from { opacity: 0; transform: translateY(22px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .fe-logo-img {
    height: 48px;
    width: auto;
    border-radius: 12px;
    margin-bottom: 20px;
    filter: drop-shadow(0 0 20px rgba(99,102,241,0.5));
  }

  .fe-title    { font-size: 1.75rem; font-weight: 900; color: #f1f5f9; letter-spacing: -0.03em; margin: 0 0 6px; }
  .fe-subtitle { font-size: 14px; color: #64748b; margin: 0 0 28px; line-height: 1.6; }

  .fe-alert { border-radius: 12px; padding: 13px 16px; font-size: 14px; margin-bottom: 20px; }
  .fe-alert-error   { background: rgba(239,68,68,0.1);  border: 1px solid rgba(239,68,68,0.25);  color: #fca5a5; }

  /* email reveal box */
  .fe-reveal {
    background: rgba(34,197,94,0.07);
    border: 1px solid rgba(34,197,94,0.2);
    border-radius: 14px;
    padding: 20px;
    text-align: center;
    margin-bottom: 20px;
  }
  .fe-reveal-label { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #4ade80; margin: 0 0 8px; }
  .fe-reveal-email { font-size: 1.2rem; font-weight: 800; color: #f1f5f9; letter-spacing: -0.01em; margin: 0; }

  .fe-group { display: flex; flex-direction: column; gap: 6px; margin-bottom: 20px; }
  .fe-label { font-size: 13px; font-weight: 700; color: #94a3b8; }

  .fe-input {
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(99,102,241,0.2);
    border-radius: 10px;
    padding: 13px 16px;
    font-size: 15px; color: #e2e8f0;
    font-family: inherit; outline: none;
    transition: border-color 0.2s, background 0.2s;
    width: 100%; box-sizing: border-box;
  }
  .fe-input::placeholder { color: #334155; }
  .fe-input:focus { border-color: rgba(99,102,241,0.5); background: rgba(99,102,241,0.05); }

  .fe-submit {
    width: 100%; padding: 14px;
    background: linear-gradient(135deg, #6366f1, #4f46e5);
    color: white; border: none; border-radius: 12px;
    font-size: 15px; font-weight: 700; cursor: pointer;
    font-family: inherit; transition: all 0.2s;
    box-shadow: 0 4px 20px rgba(99,102,241,0.35);
    margin-bottom: 20px;
  }
  .fe-submit:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 8px 30px rgba(99,102,241,0.5); }
  .fe-submit:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }

  .fe-footer-text {
    text-align: center; font-size: 14px; color: #475569; margin: 0;
  }
  .fe-footer-text a { color: #818cf8; text-decoration: none; font-weight: 600; }
  .fe-footer-text a:hover { color: #a5b4fc; }

  @media (max-width: 480px) { .fe-card { padding: 36px 20px; } }
`;

const ForgotEmail = () => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(''); setEmail('');
    try {
      const r = await axios.post(`${API_BASE_URL}/api/auth/forgot-email`, { username });
      setEmail(r.data.email);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to retrieve email');
    } finally {
      setLoading(false);
    }
  };

  const maskEmail = (em) => {
    const [local, domain] = em.split('@');
    const masked = local.length > 2
      ? local[0] + '*'.repeat(local.length - 2) + local[local.length - 1]
      : local;
    return `${masked}@${domain}`;
  };

  return (
    <div className="fe-page">
      <style>{STYLES}</style>
      <div className="fe-blob fe-blob-1" />
      <div className="fe-blob fe-blob-2" />

      <div className="fe-card">
        <img src={new URL('../assets/images/logo.png', import.meta.url).href} alt="RDRTO Logo" className="fe-logo-img" />
        <h1 className="fe-title">Forgot Email?</h1>
        <p className="fe-subtitle">Enter the username you signed up with and we'll show you the associated email address.</p>

        {error && <div className="fe-alert fe-alert-error">{error}</div>}

        {email && (
          <div className="fe-reveal">
            <p className="fe-reveal-label">✅ Your email address</p>
            <p className="fe-reveal-email">{maskEmail(email)}</p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="fe-group">
            <label className="fe-label" htmlFor="fe-username">Username</label>
            <input
              id="fe-username"
              type="text"
              className="fe-input"
              placeholder="your_username"
              value={username}
              onChange={e => setUsername(e.target.value)}
              required
            />
          </div>

          <button type="submit" className="fe-submit" disabled={loading}>
            {loading ? '⏳ Searching…' : '🔍 Find My Email'}
          </button>
        </form>

        <p className="fe-footer-text">
          Remember your email?{' '}<Link to="/login">Sign in</Link>
        </p>
        <p className="fe-footer-text" style={{ marginTop: 8 }}>
          Know your email but forgot password?{' '}<Link to="/forgot-password">Reset password</Link>
        </p>
      </div>
    </div>
  );
};

export default ForgotEmail;
